import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Grid,
  useTheme,
  alpha,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GoogleIcon from '@mui/icons-material/Google';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StarIcon from '@mui/icons-material/Star';
import HubIcon from '@mui/icons-material/Hub';

const CONNECT_CHANNELS = [
  {
    id: 'gmb',
    title: 'Google Business (GMB)',
    subtitle: 'View clinic profile & Google reviews',
    badge: 'Reviews & Maps',
    icon: <GoogleIcon sx={{ fontSize: 26, color: '#FFFFFF' }} />,
    bg: 'linear-gradient(135deg, #4285F4 0%, #34A853 50%, #FBBC05 75%, #EA4335 100%)',
    iconBg: '#4285F4',
    url: 'https://maps.google.com/?q=MyCliniq+Dental+Healthcare',
    hoverBorder: '#4285F4',
  },
  {
    id: 'facebook',
    title: 'Facebook',
    subtitle: 'Follow updates, news & announcements',
    badge: 'Official Page',
    icon: <FacebookIcon sx={{ fontSize: 28, color: '#FFFFFF' }} />,
    bg: 'linear-gradient(135deg, #1877F2 0%, #0C63D4 100%)',
    iconBg: '#1877F2',
    url: 'https://www.facebook.com/mycliniq',
    hoverBorder: '#1877F2',
  },
  {
    id: 'instagram',
    title: 'Instagram',
    subtitle: 'Clinic stories, health tips & reels',
    badge: '@mycliniq',
    icon: <InstagramIcon sx={{ fontSize: 28, color: '#FFFFFF' }} />,
    bg: 'linear-gradient(135deg, #833AB4 0%, #FD1D1D 50%, #FCB045 100%)',
    iconBg: '#E1306C',
    url: 'https://www.instagram.com/mycliniq',
    hoverBorder: '#E1306C',
  },
  {
    id: 'linkedin',
    title: 'LinkedIn',
    subtitle: 'Professional healthcare network & insights',
    badge: 'Company Network',
    icon: <LinkedInIcon sx={{ fontSize: 28, color: '#FFFFFF' }} />,
    bg: 'linear-gradient(135deg, #0A66C2 0%, #004182 100%)',
    iconBg: '#0A66C2',
    url: 'https://www.linkedin.com/company/mycliniq',
    hoverBorder: '#0A66C2',
  },
];

const ConnectModal = ({ open, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const handleOpenLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          bgcolor: theme.palette.background.paper,
          backgroundImage: 'none',
          boxShadow: isDark
            ? '0 24px 60px rgba(0, 0, 0, 0.8)'
            : '0 24px 60px rgba(15, 23, 42, 0.15)',
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          p: 3,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '12px',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <HubIcon />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: theme.palette.text.primary, lineHeight: 1.2 }}>
              Want to connect with Us!
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              Stay updated, explore reviews on Google, and join our healthcare network
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            borderRadius: '10px',
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Content - Social & GMB Channels */}
      <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Grid container spacing={2}>
          {CONNECT_CHANNELS.map((item) => (
            <Grid item xs={12} sm={6} key={item.id}>
              <Box
                onClick={() => handleOpenLink(item.url)}
                sx={{
                  p: 2,
                  height: '100%',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  bgcolor: theme.palette.background.subtle,
                  border: `1.5px solid ${theme.palette.divider}`,
                  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    borderColor: item.hoverBorder,
                    transform: 'translateY(-3px)',
                    boxShadow: isDark
                      ? '0 12px 28px rgba(0, 0, 0, 0.6)'
                      : '0 12px 28px rgba(0, 0, 0, 0.08)',
                    bgcolor: alpha(item.hoverBorder, isDark ? 0.12 : 0.04),
                    '& .open-icon': {
                      transform: 'translateX(2px) translateY(-2px)',
                      color: item.hoverBorder,
                    },
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '12px',
                      background: item.bg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}
                  >
                    {item.icon}
                  </Box>

                  <Tooltip title="Visit Page">
                    <OpenInNewIcon
                      className="open-icon"
                      sx={{
                        fontSize: 18,
                        color: theme.palette.text.secondary,
                        transition: 'transform 0.2s ease, color 0.2s ease',
                      }}
                    />
                  </Tooltip>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 800,
                        color: theme.palette.text.primary,
                        fontSize: '14px',
                      }}
                    >
                      {item.title}
                    </Typography>
                    {item.id === 'gmb' && (
                      <StarIcon sx={{ fontSize: 16, color: '#F59E0B' }} />
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      display: 'block',
                      lineHeight: 1.35,
                      fontSize: '11.5px',
                    }}
                  >
                    {item.subtitle}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    pt: 1,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: item.hoverBorder,
                      fontSize: '11px',
                      letterSpacing: '0.3px',
                    }}
                  >
                    {item.badge}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      fontSize: '11px',
                    }}
                  >
                    Open ↗
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{
          p: 2.5,
          px: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.subtle,
        }}
      >
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
          Need clinic assistance? <Box component="span" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>support@mycliniq.app</Box>
        </Typography>

        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            px: 3,
            py: 0.8,
            borderRadius: '10px',
            fontWeight: 700,
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText || '#FFFFFF',
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
            '&:hover': { bgcolor: theme.palette.primary.dark },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConnectModal;
